<template>
  <v-card flat outlined class="ma-4">
    <v-card-title>
      {{ $t("users.settings.menuItems.settings") }}
      <v-spacer></v-spacer>
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <span v-on="on">
            <v-icon :disabled="editPreferences" @click="editPreferences = true"
              >mdi-pencil</v-icon
            >
          </span>
        </template>
        <span>{{ $t("common.actions.edit") }}</span>
      </v-tooltip>
    </v-card-title>
    <v-card-text>
      <v-form
        ref="editUserPreferences"
        autocomplete="off"
        v-model="valid"
        :disabled="!editPreferences"
      >
        <v-row>
          <v-col cols="12" lg="6" class="pb-0">
            <v-select
              v-model="language"
              :label="$t('common.language')"
              :items="languageItems"
              item-text="name"
              item-value="code"
              prepend-icon="mdi-translate"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" lg="6" class="pb-0">
            <v-select
              v-model="unitOption"
              :label="$t('users.settings.preferences.units')"
              :items="unitItems"
              item-text="name"
              item-value="value"
              prepend-icon="mdi-ruler"
            ></v-select>
          </v-col>
        </v-row>
        <v-row v-if="canToggleWeeklyReports">
          <v-col cols="12" lg="6" class="pb-0">
            <v-row no-gutters>
              <v-col cols="auto" class="pr-3 pt-2">
                <v-icon>mdi-email-newsletter</v-icon>
              </v-col>
              <v-col>
                <v-label style="font-size: 12px;">
                  {{ $t("users.settings.notifications.sendWeeklyReports") }}
                </v-label>
                <v-checkbox
                  v-model="sendWeeklyReports"
                  hide-details
                  class="mt-1 pt-0"
                  :label="
                    $t('users.settings.notifications.sendWeeklyReportsDesc')
                  "
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-card-actions v-if="editPreferences" class="pr-4 pb-4">
      <v-spacer></v-spacer>
      <v-btn
        class="mr-2"
        :disabled="!valid || isUpdating"
        :loading="isUpdating"
        @click.stop="onSavePreferences"
      >
        {{ $t("common.actions.save") }}
      </v-btn>
      <v-btn :disabled="isUpdating" @click.stop="onCancelPreferences">
        {{ $t("common.actions.cancel") }}
      </v-btn>
    </v-card-actions>

    <v-snackbar
      v-model="snackbar"
      :timeout="2000"
      top
      dark
      color="green darken-1"
    >
      {{ snackbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn text @click="snackbar = false" v-bind="attrs">
          {{ $t("common.actions.close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import languages from "@/utils/languages"
import { mapActions } from "vuex"
import fetchClient from "@/utils/fetchClient"

export default {
  name: "UserPreferences",
  data() {
    return {
      valid: false,
      snackbar: false,
      editPreferences: false,
      isUpdating: false,
      sendWeeklyReports: true,
      languageItems: languages.supported,
      language: this.$store.getters.langCode,
      unitOption: "metric",
      initialView: "map"
    }
  },
  components: {},
  created() {},
  mounted() {
    this.fetchUserDetails()
  },
  computed: {
    snackbarText() {
      return this.$t("common.saveSuceeded")
    },
    loggedInUser() {
      return this.$store.getters.loggedInUser
    },
    canToggleWeeklyReports() {
      return (
        this.$store.getters.isAdmin ||
        this.$store.getters.isService ||
        this.$store.getters.isLocalAdmin
      )
    },
    unitItems() {
      return [
        {
          name: this.$t("common.enums.measurementSystem.metric"),
          value: "metric"
        },
        {
          name: this.$t("common.enums.measurementSystem.imperial"),
          value: "imperial"
        }
      ]
    }
  },
  watch: {},
  methods: {
    ...mapActions(["setError"]),

    async fetchUserDetails() {
      await this.$store.dispatch("fetchUserDetails", {
        tenantUid: this.loggedInUser.tenantUid,
        uid: this.loggedInUser.uid
      })
      const res = this.$store.getters.userDetails

      if (res != null) {
        if (res.preferences != null) {
          if (res.preferences?.language) {
            this.language = res.preferences.language
          }
          if (res.preferences?.units) {
            this.unitOption = res.preferences.units
          }
          if (res.preferences?.initialView) {
            this.initialView = res.preferences.initialView
          }
        }
        if (res.notifications !== null) {
          this.sendWeeklyReports = res.notifications?.weeklyReports
        }
      }
    },
    async onSavePreferences() {
      this.isUpdating = true
      try {
        if (this.canToggleWeeklyReports) {
          // Only save weekly report settings if user is allowed to change it.
          // Users having user role cannot get weekly reports, thus it's hidden.
          const url = `/api/v1/tenants/${this.loggedInUser.tenantUid}/users/${this.loggedInUser.uid}/notifications`
          await fetchClient(this.$i18n).putOrThrow(url, {
            weeklyReports: this.sendWeeklyReports
          })
        }

        let preferences = {
          language: this.language,
          units: this.unitOption,
          initialView: this.initialView
        }
        await this.$store
          .dispatch("updateUserPreferences", preferences)
          .catch(() => {
            // Error already displayed by dispatched action.
          })
        this.$root.setLanguage(this.language) //Set language after "updateUserPreferences" is completed, to not update language in database 2nd time
        this.snackbar = true
        this.editPreferences = false
      } catch (error) {
        this.setError(error)
      } finally {
        this.isUpdating = false
      }
    },
    async onCancelPreferences() {
      this.editPreferences = false
      await this.fetchUserDetails()
    }
  }
}
</script>

<style></style>
